import * as Yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import { forgotPassword } from 'actions/users';
import { useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Browser from 'helpers/browser';
import { Actions, Errors, Instructions, LinkWrapper } from './Styled';
import { Formik } from 'components/EnhancedForm';
import { Button, Text, TextField } from '@teachstonellc/component-library';
import FocusFirstInvalidField from 'components/FocusFirstInvalidField';

const validationSchema = Yup.object({
  email: Yup.string()
    .email(() => i18n.ft(messages.errors.email.invalid))
    .required(() => i18n.ft(messages.errors.email.required))
});

const initialValues = {
  email: ''
};

const ForgotPasswordForm = props => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState();
  const [email, setEmail] = useState();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(state => state.user);
  const emailInputRef = useRef(null);
  const loginPath = Browser.containsCookie('course_merchant')
    ? RouteHelpers.getPath('users-cm-login')
    : RouteHelpers.getPath('users-login');
  const isForgotPassword =
    window.location.pathname === RouteHelpers.getPath('users-forgot-password');

  function instructions() {
    return isForgotPassword
      ? i18n.ft(messages.resetInstructions)
      : i18n.ft(messages.setInstructions);
  }

  useEffect(() => {
    // Redirect user to dashboard if already logged in
    if (currentUser.id) {
      navigate(RouteHelpers.getPath('app'), { replace: true });
    }
  }, [currentUser.id, navigate]);

  function handleSubmit(values, formik) {
    setLoading(true);

    const user = {
      email: values.email
    };

    forgotPassword({ user })
      .then(() => {
        setLoading(false);
        setSuccess(true);
        setEmail(values.email);
      })
      .catch(({ response }) => {
        setLoading(false);
        setErrors(i18n.ft(messages.errors.unknown));
      });
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {formik => {
        const isError = Boolean(formik.touched.email && formik.errors.email);

        return (
          <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <FocusFirstInvalidField
              submitCount={formik.submitCount}
              fields={[{ isInvalid: isError, inputRef: emailInputRef }]}
            />

            <Text variant="h2" className="text-left pb-2">
              {isForgotPassword
                ? i18n.ft(messages.forgotYourPassword)
                : i18n.ft(messages.enterEmail)}
            </Text>

            {errors && <Errors>{errors}</Errors>}

            {success ? (
              <React.Fragment>
                <Instructions
                  dangerouslySetInnerHTML={{
                    __html: i18n.ft(messages.confirmation, {
                      email: email
                    })
                  }}
                />
                <LinkWrapper>
                  <Button
                    fullWidth
                    variant="primary"
                    shape="rounded"
                    loading={loading}
                    disabled={loading}
                    as={Link}
                    to={loginPath}
                  >
                    {i18n.ft(messages.backToLogin)}
                  </Button>
                </LinkWrapper>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Instructions
                  dangerouslySetInnerHTML={{
                    __html: instructions()
                  }}
                />

                <TextField
                  {...formik.getFieldProps('email')}
                  ref={emailInputRef}
                  autoFocus
                  type="email"
                  autoComplete="email"
                  label={i18n.ft(messages.email)}
                  placeholder={i18n.ft(messages.email)}
                  aria-invalid={isError}
                  error={isError ? formik.errors.email : undefined}
                />

                <Actions className="mt-4">
                  <Button
                    as={Link}
                    to={RouteHelpers.getPath('users-login')}
                    variant="secondary"
                    shape="rounded"
                  >
                    {i18n.ft(messages.cancel)}
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    shape="rounded"
                    loading={loading}
                    disabled={loading}
                  >
                    {i18n.ft(messages.send)}
                  </Button>
                </Actions>
              </React.Fragment>
            )}
          </form>
        );
      }}
    </Formik>
  );
};

export default ForgotPasswordForm;
